<template>
  <main-input-dialog
    :is-visible.sync="computedIsVisible"
    :title-translation="$i18n.t('dialog.uploadFeatures')"
    agree-text="dialog.agreeImport"
    :loading="isDialogLoading"
    dialog-width="32%"
    :custom-is-valid="!!fileAttributes.length && !!currentFeatureCollection"
    @agree="!datasource ? handleMultipleUpload() : handleAgreeUpload()"
    :inputs="dialogInputs"
  >
    <template v-for="(input, idx) of dialogInputs" #[`${input.name}.input`]="data">
      <template v-if="input.name === 'errorMessage'">
        <v-row :key="idx" no-gutters>
          <v-col cols="12">
            <v-alert
              type="error"
              icon="mdi-alert-octagon-outline"
              dense
              text
              class="text-left mb-6 text-caption"
              style="width: 100%"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-medium">{{ $i18n.t('table.rziMonitoring.importError') }}</span>
                <span>
                  <v-icon color="error" @click="isErrorExpanded = !isErrorExpanded">{{
                    isErrorExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                  }}</v-icon>
                </span>
              </div>
              <div v-if="isErrorExpanded">
                <ul>
                  <template v-for="[key, value] in Object.entries(errorData) ?? {}">
                    <li v-for="(item, index) of value ?? []" :key="`${key}-${index}`">
                      {{ $i18n.t(`table.rziMonitoring.importError_${key}`, { ...item }) }}
                    </li>
                  </template>
                </ul>
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </template>
      <v-col v-else cols="6" :key="idx">
        <template v-if="input.name === 'mappingHeader'">
          <v-row no-gutters class="mt-6 mb-6">
            <v-col class="text-left">
              <div class="font-weight-medium">{{ input.title }}</div>
              <div class="text-caption text--secondary font-italic">{{ input.subtitle }}</div>
            </v-col>
          </v-row>
        </template>
        <template v-else-if="input.name === 'file'">
          <file-input
            ref="file"
            v-bind="input"
            :events="{
              'vdropzone-files-added': onFileAdded,
              'vdropzone-error-multiple': onUploadError,
            }"
          ></file-input>
        </template>
        <template v-else>
          <data-input v-if="input.name === 'srid'" v-bind="data" v-model="selectedSrid" />
          <data-input v-else v-bind="data" v-model="attributesMapping[input.name]" />
        </template>
      </v-col>
    </template>
  </main-input-dialog>
</template>

<script>
import { get, call } from 'vuex-pathify';
import FileInput from '@/components/FileInput';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import shp from 'shpjs';

export default {
  name: 'RziUploadGeometryDialog',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    FileInput,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    currentMetadata: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
    },
    /** Mass import if datasource not provided */
    datasource: {
      type: String,
      required: false,
      default: null,
    },
    staticProperties: {
      type: Object,
    },
  },
  data: () => ({
    isDialogLoading: false,
    formSchema: [],
    attributesMapping: {},
    fileAttributes: [],
    currentFeatureCollection: null,
    selectedSrid: null,
    errorData: null,
    isErrorExpanded: false,
  }),
  computed: {
    layers: get('layers/layers'),
    layersMetadata: get('layers/metadata'),
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    computedEpsgItems() {
      return this.$_objectToArray(this.coordinatesEpsgs);
    },
    allowedGeometryTypes() {
      return this.datasource
        ? [this.layersMetadata[this.datasource].geometry_type?.toLowerCase()]
        : ['point', 'polygon'];
    },
    datasources() {
      return this.datasource
        ? [this.datasource]
        : [
            this.currentMetadata.monitoringYears.datasource,
            this.currentMetadata.tableViews.geometries.datasources.polygon,
          ];
    },
    dialogInputs() {
      return [
        ...(this.errorData ? [{ name: 'errorMessage', leftCol: null }] : []),
        {
          name: 'srid',
          translationPath: 'dialog.body.coordinateSystem',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'autocomplete',
          },
          fixedHeight: false,
          itemText: 'text',
          items: this.computedEpsgItems,
          itemValue: 'srid',
        },
        {
          name: 'file',
          translationPath: 'dialog.body.file',
          options: this.dropzoneOptions,
          showAddedFiles: true,
          expandedFilesExtensions: this.uploadExtensions,
          buttonSubheaders: this.fileInputSubheadersTranslations,
        },
        ...(this.currentFeatureCollection && this.fileAttributes.length
          ? [
              {
                name: 'mappingHeader',
                leftCol: null,
                title: this.$i18n.t('dialog.body.attributesMapping'),
                subtitle: this.$i18n.t('dialog.body.attributesMappingSubtitle'),
              },
              ...this.formSchema.map(attr => ({
                name: attr.attribute,
                straightTitle: attr.label,
                dataType: {
                  name: 'select',
                },
                items: this.fileAttributes,
                rules: [v => !!v || 'rules.required'],
              })),
            ]
          : []),
      ];
    },
    uploadExtensions() {
      return [
        {
          name: 'shapefile',
          url: 'x',
          exts: ['shp', 'shx', 'dbf'],
          canExtDuplicate: false,
          minFiles: 3,
          maxFiles: 3,
        },
        {
          name: 'geojson',
          exts: ['geojson'],
          url: 'x',
          canExtDuplicate: false,
          minFiles: 1,
          maxFiles: 1,
        },
      ];
    },
    dropzoneOptions() {
      const self = this;
      return {
        url: 'x',
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: ['.shp', '.dbf', '.shx', '.geojson'].join(','),
        uploadMultiple: true,
        maxFiles: 3,
        parallelUploads: 3,
        paramName(i) {
          const files = self.$refs.file[0]?.$refs.dropzone.getAcceptedFiles();
          const file = files[i];
          return file.name.split('.').pop();
        },
      };
    },
    fileInputSubheadersTranslations() {
      return [
        {
          type: 'straight',
          value: this.$i18n.t('dialog.dataSourceRequiredFiles', {
            value: 'Shapefile(dbf, shp, shx)/GeoJSON',
          }),
        },
        {
          type: 'path',
          value: 'dialog.dataSourceCoding',
        },
      ];
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (!nV) {
          this.selectedSrid = null;
          this.attributesMapping = {};
          this.getFileDropzone()?.removeAllFiles();
          return;
        }
        this.prepareData();
      },
    },
  },
  methods: {
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    uploadGeometries: call('rziData/uploadGeometries'),
    getFileDropzone() {
      return [this.$refs.file].flat()?.[0]?.$refs.dropzone ?? null;
    },
    prepareData() {
      this.getFileDropzone()?.removeAllFiles();
      this.selectedSrid = this.coordinatesEpsgs['EPSG:2180'].srid ?? null;
      this.fileAttributes = [];
      this.formSchema = this.datasources
        .reduce((acc, dt) => {
          return [
            ...acc,
            ...this.getForm(null, this.layersMetadata[dt].attributes_schema.attributes, {
              filteredOutAttributes: this.layersMetadata[dt].attributes_schema.attributes
                .filter(attr => !this.currentMetadata.massImportAttributes.includes(attr.name))
                .map(attr => attr.name),
            }),
          ];
        }, [])
        .filter(attr => attr.editable);
    },
    successCloseDialog() {
      this.isDialogLoading = this.computedIsVisible = false;
    },
    async handleMultipleUpload() {
      this.isDialogLoading = true;
      try {
        const payload = new FormData();
        Object.entries({ ...(this.rawFiles ?? {}), ...this.attributesMapping }).forEach(([key, file]) => {
          payload.append(key, file);
        });
        await this.uploadGeometries(payload);
        this.$emit('done', this.successCloseDialog);
      } catch (error) {
        if (error.status === 409) {
          this.errorData = error.data?.data;
          this.isErrorExpanded = false;
        }
        this.isDialogLoading = false;
      }
    },
    async handleAgreeUpload() {
      this.isDialogLoading = true;
      const geometryAttributeName = this.layersMetadata[this.datasource].attributes_schema.geometry_name;
      const copyData = { ...this.currentFeatureCollection };
      const newFeatures = copyData.features.reduce((acc, ft) => {
        const newProperties = Object.entries(this.attributesMapping).reduce(
          (accProps, [dtAttribute, mappedAttribute]) => {
            accProps[dtAttribute] = ft.properties[mappedAttribute];
            return accProps;
          },
          {
            ...this.staticProperties,
            [geometryAttributeName]: {
              ...ft.geometry,
              crs: copyData.crs
                ? copyData.crs
                : {
                    type: 'name',
                    properties: {
                      name: `EPSG:${this.selectedSrid}`,
                    },
                  },
            },
          }
        );
        acc.push(newProperties);
        return acc;
      }, []);
      const payload = {
        data_source_name: this.datasource,
        insert: newFeatures,
      };
      try {
        await this.editMultipleDataSourceFeatures(payload);
        this.$emit('done', this.successCloseDialog, { datasource: this.datasource });
      } catch {
        this.isDialogLoading = false;
      }
    },
    onUploadError() {
      this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
      this.currentFeatureCollection = null;
      this.fileAttributes = [];
    },
    async onFileAdded(files) {
      const filesByExtension = [...files].reduce((acc, file) => {
        const ext = file.name.split('.').pop();
        acc[ext] = file;
        return acc;
      }, {});
      let fileFeatureCollection;
      if (filesByExtension.geojson) {
        const geojsonBuffer = await filesByExtension.geojson.text();
        fileFeatureCollection = JSON.parse(geojsonBuffer);
      } else {
        if (!filesByExtension.shp || !filesByExtension.dbf) return;
        const [shpBuffer, dbfBuffer] = await Promise.all([
          filesByExtension.shp.arrayBuffer(),
          filesByExtension.dbf.arrayBuffer(),
        ]);
        fileFeatureCollection = shp.combine([shp.parseShp(shpBuffer), shp.parseDbf(dbfBuffer)]);
      }
      if (
        fileFeatureCollection.features.find(ft => !this.allowedGeometryTypes.includes(ft.geometry.type.toLowerCase()))
      ) {
        this.onUploadError();
        return this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.geometryTypeMismatch'),
        });
      }
      this.rawFiles = { ...filesByExtension };
      this.fileAttributes = Object.keys(fileFeatureCollection.features[0].properties);
      this.currentFeatureCollection = fileFeatureCollection;
    },
  },
};
</script>
